import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * `refs` accepts more than one ref so a trigger can be paired with content
 * portaled elsewhere in the DOM (e.g. a dropdown rendered into `document.body`
 * to escape a modal's `overflow-hidden`) — a click inside either counts as
 * "inside". Pass a `useMemo`-stabilized array (ref objects themselves are
 * already stable) so the listener isn't torn down and re-added every render.
 */
export const useClickOutside = <T extends HTMLElement>(
  refs: RefObject<T | null> | Array<RefObject<T | null>>,
  onClickOutside: () => void,
  enabled = true
) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const refList = Array.isArray(refs) ? refs : [refs];

    const handleMouseDown = (event: MouseEvent) => {
      const mountedRefs = refList
        .map((ref) => ref.current)
        .filter((current): current is T => current !== null);

      // Matches the original single-ref behavior: an unmounted ref never
      // triggers `onClickOutside` on its own — only a click outside every
      // currently-mounted ref does.
      if (!mountedRefs.length) {
        return;
      }

      const isInsideAny = mountedRefs.some((current) =>
        current.contains(event.target as Node)
      );

      if (!isInsideAny) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [refs, onClickOutside, enabled]);
};
